import NiceModal, { useModal } from '@ebay/nice-modal-react'
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUpdateCompanyUserMutation } from '../../hooks/useMutations/useCompaniesMutations'
import { useSnackbar } from '../../hooks/useSnackbar'
import { LoaderButton } from '../LoaderButton'

export const ChangeJobTitleModal = NiceModal.create(
  ({
    companyId,
    userId,
    workspaceId
  }: {
    companyId: string
    userId: string
    workspaceId?: string
  }) => {
    const modal = useModal()
    const { t } = useTranslation()
    const [newJobTitle, setNewJobTitle] = useState('')
    const { showSnackbar } = useSnackbar()

    const updateCompanyUserMutation = useUpdateCompanyUserMutation(
      companyId,
      workspaceId
    )

    const handleSave = () => {
      updateCompanyUserMutation.mutate(
        {
          userId,
          data: { jobTitle: newJobTitle }
        },
        {
          onSuccess: () => {
            showSnackbar({
              message: t('change_job_title_modal.success'),
              severity: 'success'
            })
          },
          onError: () => {
            showSnackbar({
              message: t('change_job_title_modal.error'),
              severity: 'error'
            })
          },
          onSettled: () => {
            modal.remove()
          }
        }
      )
    }

    return (
      <Dialog
        open={modal.visible}
        onClose={() => modal.remove()}
        sx={{
          '& .MuiDialog-paper': {
            width: '32.5625rem',
            padding: '1.5rem',
            borderRadius: '.25rem'
          }
        }}
      >
        <DialogTitle sx={{ py: '1.5rem' }}>
          {t('change_job_title_modal.title')}
        </DialogTitle>
        <DialogContent sx={{ padding: '0' }}>
          <Stack
            direction='column'
            spacing='1rem'
            sx={{ padding: '1rem 1.5rem' }}
          >
            <Typography variant='body1' sx={{ paddingBottom: '0.75rem' }}>
              {t('change_job_title_modal.description')}
            </Typography>
            <FormControl fullWidth>
              <TextField
                label={t('change_job_title_modal.label')}
                value={newJobTitle}
                onChange={e => setNewJobTitle(e.target.value)}
                fullWidth
                size='small'
              />
            </FormControl>

            {workspaceId && (
              <Alert severity='warning'>
                <AlertTitle>
                  {t('change_job_title_modal.alert_title')}
                </AlertTitle>
                {t('change_job_title_modal.alert_description')}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            color='secondary'
            onClick={() => modal.remove()}
            sx={{
              textTransform: 'none',
              width: '14rem',
              padding: '.5rem 1.375rem',
              color: 'secondary.light',
              backgroundColor: 'custom.semiTransparentIndigo'
            }}
          >
            {t('common.cancel')}
          </Button>
          <LoaderButton
            variant='contained'
            loading={updateCompanyUserMutation.isPending}
            label={
              updateCompanyUserMutation.isPending
                ? t('change_job_title_modal.button_loading')
                : t('change_job_title_modal.button')
            }
            sx={{
              width: '14rem',
              textTransform: 'none',
              padding: '.5rem 1.375rem',
              color: 'rgba(0, 0, 0, 0.87)'
            }}
            onClick={handleSave}
            disabled={!newJobTitle || updateCompanyUserMutation.isPending}
          />
        </DialogActions>
      </Dialog>
    )
  }
)
